import {
  availableGoals,
  CONFIGURATION_TYPE,
  configurationNativeTypes,
  ConfigurationValues,
  defaultConfigurationValues,
  HasShowReviewAppPopup,
  HomeWelcomeModalInfos,
  KEYBOARD_TYPE,
  OnboardingConfig,
  PlayerDisplayModeOptions,
  PlayerIndicationDisplayModeOptions,
  SavedGoalSetting,
} from './UserConfigurationTypes';
import {analyticsController} from '../analytics/AnalyticsController';
import {DayNotificationConfig, DAYS_LABEL} from './NotificationSessionTypes';
import {getAssetItem} from 'src/utils/helpers.js'

export default class UserConfigurations {
  private configurationsValues = {...defaultConfigurationValues};
  public defaultCurrentWeeklyFreeActions: number = defaultConfigurationValues.CURRENT_WEEKLY_FREE_ACTIONS;
  public defaultCurrentDailyFreeActions: number = defaultConfigurationValues.CURRENT_DAILY_FREE_ACTIONS;
  public defaultCurrentWeeklyFreeTeacherMessages: number = defaultConfigurationValues.CURRENT_WEEKLY_FREE_TEACHER_MESSAGES;
  public isLangDefaultUsed = true;

  getConfigurationsValues(): ConfigurationValues {
    return this.configurationsValues;
  }

  // Initialisation
  updateDataFromJson(configurations: any[]) {
    if (Object.keys(this.configurationsValues).length !== Object.keys(configurations).length) {
      console.warn('Expected configurations & actual configurations has different sizes');
    }
    
    Object.keys(this.configurationsValues).forEach((key: string) => {
      const configurationType = key as CONFIGURATION_TYPE;
      this.configurationsValues[configurationType] = this.getConfigFromJson(configurationType, configurations);

      if(configurationType === 'CURRENT_WEEKLY_FREE_ACTIONS') {
        this.defaultCurrentWeeklyFreeActions = this.getConfigFromJson(configurationType, configurations, true);
      }

      if(configurationType === 'CURRENT_WEEKLY_FREE_TEACHER_MESSAGES') {
        this.defaultCurrentWeeklyFreeTeacherMessages = this.getConfigFromJson(configurationType, configurations, true);
      }

      if(configurationType === 'CURRENT_DAILY_FREE_ACTIONS') {
        this.defaultCurrentDailyFreeActions = this.getConfigFromJson(configurationType, configurations, true);
      }

      if(configurationType === 'LANG') {
        const config = configurations.find((config) => config.name === configurationType);
        if (config) {
          this.isLangDefaultUsed = config.is_default_used 
        }
      }

      analyticsController.addAmplitudeIdentityInformation(
        configurationType,
        this.displayableConfiguration(this.configurationsValues[configurationType], configurationType)
      );
    });
  }

  displayableConfiguration(values: any, type: CONFIGURATION_TYPE): string {
    if (type === 'SESSION_NOTIFICATIONS') {
      let result = '';
      for (const day in values) {
        if (values[day] && day !== 'time') {
          // @ts-ignore
          result += DAYS_LABEL[day] + ', ';
        }
      }
      values = result + values.time;
    }
    else if (type === 'USER_GOALS') {
      // @ts-ignore
      values = values
        .map((obj: {id: number}) => {
          const goal = availableGoals.find((labelObj) => labelObj.id === obj.id);
          return goal ? goal.label : null;
        })
        .filter((label: null | string) => label !== null)
        .join(', ');
    }
    else if (type === 'USER_REVIEW') {
      return values === null ? '' : `${values.rating}, ${values.review}`;
    }
    return values?.toString();
  }

  private getConfigFromJson(configurationType: CONFIGURATION_TYPE, configurations: any[], defaultValue = false): any {
    const config = configurations.find((config) => config.name === configurationType);
    const valueField = defaultValue ? 'default' : 'value';
    if (!config) {
      console.error("Can't load config : " + configurationType);
      return null;
    }

    switch (configurationNativeTypes[configurationType]) {
      case 'boolean':
        return ['true', '1', 'yes', 'y'].find((val) => val === config[valueField]) !== undefined;
      case 'number':
        return parseInt(config[valueField]);
      case 'string':
        return config[valueField];
      case 'json':
        return JSON.parse(config[valueField]);
    }
  }

  updateLocalConfigByName(name: CONFIGURATION_TYPE, value: any) {
    this.configurationsValues[name] = value;
  }

  // Admin
  get teamMember(): string {
    return this.configurationsValues.TEAM_MEMBER;
  }

  get canAccessDraftCatalog(): boolean {
    return this.configurationsValues.ACCESS_DRAFT_CATALOG || this.teamMember === 'admin';
  }

  get catalogType(): string {
    return this.configurationsValues.SELECTED_CATALOG_TYPE;
  }

  // Player
  get onNoteDisplay(): PlayerIndicationDisplayModeOptions {
    return this.configurationsValues.ON_NOTE_DISPLAY;
  }

  // Onboarding
  get onboardingConfig(): OnboardingConfig {
    return this.configurationsValues.ONBOARDING;
  }

  // User Profile
  get keyboardType(): KEYBOARD_TYPE {
    return this.configurationsValues.KEYBOARD_TYPE;
  }

  get isSinger(): boolean {
    return this.configurationsValues.IS_SINGER;
  }

  get sessionNotifications(): DayNotificationConfig {
    return this.configurationsValues.SESSION_NOTIFICATIONS;
  }

  get goals(): SavedGoalSetting[] {
    return this.configurationsValues.USER_GOALS;
  }

  get teacher(): string {
    return this.configurationsValues.SELECTED_TEACHER !== ''
      ? this.configurationsValues.SELECTED_TEACHER
      : 'teacher_abstract';
  }

  get teacherUrl(): string {
    return getAssetItem('images/teachers/' + this.teacher + '.png');
  }

  // Audio logs collection
  get isAudioLogsCollectionAuthorized() {
    return this.configurationsValues.AUDIO_LOGS_COLLECTION_AUTHORIZATION;
  }

  get playerDisplayMode(): PlayerDisplayModeOptions {
    return this.configurationsValues.PLAYER_DISPLAY_MODE;
  }

  get pieceCourseIds(): number[] {
    return this.configurationsValues.PIECE_COURSES
  }

  get homeWelcomeModalInfos(): HomeWelcomeModalInfos {
    return this.configurationsValues.HOME_WELCOME_MODAL_INFOS;
  }

  get notInterestingVersions(): number[] {
    return this.configurationsValues.NOT_INTERESTING_VERSIONS
  }

  get currentWeeklyFreeActions(): number {
    return this.configurationsValues.CURRENT_WEEKLY_FREE_ACTIONS
  }

  get currentWeeklyFreeTeacherMessages(): number {
    return this.configurationsValues.CURRENT_WEEKLY_FREE_TEACHER_MESSAGES
  }

  get currentDailyFreeActions(): number {
    return this.configurationsValues.CURRENT_DAILY_FREE_ACTIONS
  }

  get hasShowReviewAppPopup(): HasShowReviewAppPopup {
    return this.configurationsValues.HAS_SHOW_REVIEW_APP_POPUP
  }

  get lang(): string {
    return this.configurationsValues.LANG
  }

  // Non-Reactive accessor
  public getConfig(type: CONFIGURATION_TYPE) {
    return this.configurationsValues[type];
  }
}
